// frontend/src/hooks/useTabularEntity.js
//
// What the import dialog is allowed to say about one entity, read from the
// server that will actually run the import (GET /api/tabular-entities/,
// backed by api.tabular_registry).
//
// Two facts come from here, never from the page: whether the import can
// create records, and which columns a "clear blanks" checkbox would empty.
//
// Deployment-static, so it is cached for the session and never scoped to a
// store.

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { apiJson } from '../queries/api';
import * as keys from '../queries/keys';

export function useTabularEntities() {
  return useQuery({
    queryKey: keys.reference.tabularEntities(),
    queryFn: () => apiJson.get('/api/tabular-entities/'),
    staleTime: Infinity,
  });
}

/**
 * Describe one entity's import.
 *
 * Returns `{ entity, updateOnly, clearableLabels, isLoading, error }`.
 *
 * `error` is set both when the request fails AND when the key names no
 * registered entity. Neither is smoothed over: a dialog that cannot state
 * what an import does must say so rather than fall back to a plausible
 * description, because every plausible default here is a promise to a
 * merchant that the server has not agreed to keep.
 */
export function useTabularEntity(entityKey) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useTabularEntities();

  const entity = data?.[entityKey] ?? null;
  const missing = !isLoading && !error && !entity;

  return {
    entity,
    isLoading,
    error: error || (missing ? new Error(`Unknown entity '${entityKey}'`) : null),
    updateOnly: entity?.mode === 'update_only',
    // The merchant reads these in their own language. Every clearable column
    // is required to carry an i18n key (pinned by
    // test_clearable_columns_carry_an_i18n_key), so there is deliberately no
    // fall back to the English label here — that fallback would silently
    // paper over the one failure the contract test exists to catch.
    clearableLabels: (entity?.clearable_columns || []).map(
      (column) => t(column.i18n_key),
    ),
  };
}
